/**
 * Shared OSAT constants + per-browser-session "evaluated" flag helpers.
 */

/**
 * sessionStorage key for the per-browser-session "OSAT evaluated" flag.
 *
 * Marked once by the OSAT trigger in `AppRoutes` (`src/App.tsx`) as soon as the
 * eligibility query SETTLES (success OR error) after full authentication, and
 * read on subsequent renders to prevent re-evaluation within the same tab
 * session. Use the `getOsatEvaluated` / `markOsatEvaluated` / `clearOsatEvaluated`
 * accessors below rather than touching sessionStorage directly — they are the
 * structural twin of `alertBarSnooze` (another per-session sessionStorage
 * UX-suppression flag) and are resilient to unavailable storage.
 *
 * Design (Bug 859323 — overrides the interactive-only scope of #804759):
 * - The survey opens once per browser session for an eligible user on the FIRST
 *   settled eligibility after full auth, INDEPENDENT of auth path. Silent auth
 *   (`ssoSilent`, `acquireTokenSilent`) is no longer excluded, because the
 *   trigger no longer keys off `handleRedirectPromise` — which silent auth never
 *   returns through, so those users were never surveyed before.
 * - A plain F5 / reload keeps this flag (sessionStorage persists within the tab
 *   session) ⇒ no re-show (#806378).
 * - Cleared on logout / session-clear (`clearOsatEvaluated`, alongside
 *   `clearSnoozedIds`) so the flag cannot leak to the next account in the same
 *   tab on a shared device (a shared operator workstation would otherwise never
 *   survey user B after user A).
 * - The flag is set the moment eligibility settles regardless of the result, so a
 *   not-eligible / errored settle cannot be replayed into an open by a later
 *   refetch flip within the same session.
 *
 * Scope of the guarantee: this flag bounds re-show WITHIN a browser tab-session
 * only. Across sessions (new tab / next day) it is fresh, so the survey is
 * re-evaluated. The server (`lastDismissedDate`) is the once-per-day authority —
 * but only for users who actually dismiss or submit; a survey that was shown and
 * simply ignored records nothing server-side, so it can reappear in a new session
 * the same day (a deliberate behaviour change over the prior interactive-only
 * trigger — see the 859323 PR discussion).
 */
export const OSAT_EVALUATED_KEY = 'osat:evaluated';

/**
 * Whether OSAT has already been evaluated in this browser tab-session.
 * Resilient to unavailable sessionStorage (e.g. hardened privacy mode) — treats
 * it as "not evaluated" so the trigger can still run.
 */
export const getOsatEvaluated = (): boolean => {
  try {
    return sessionStorage.getItem(OSAT_EVALUATED_KEY) === '1';
  } catch {
    return false;
  }
};

/** Mark OSAT as evaluated for this tab-session. No-op if storage is unavailable. */
export const markOsatEvaluated = (): void => {
  try {
    sessionStorage.setItem(OSAT_EVALUATED_KEY, '1');
  } catch {
    // sessionStorage unavailable — the guard degrades to re-evaluating next render.
  }
};

/**
 * Clear the per-session flag. Called on logout / session-clear (alongside
 * `clearSnoozedIds`) so the next account in the same tab on a shared device is
 * evaluated for OSAT rather than silently excluded (859323 review).
 */
export const clearOsatEvaluated = (): void => {
  try {
    sessionStorage.removeItem(OSAT_EVALUATED_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
};

/**
 * sessionStorage key for "the OSAT survey actually OPENED in this tab-session".
 *
 * Distinct from `OSAT_EVALUATED_KEY` on purpose. That flag is set the moment eligibility settles,
 * before the `eligible === true` branch, so it is also set for users who never see the survey —
 * reading it as "OSAT was shown" would suppress anything gated on it for every user. `isOpen` on
 * the modal context is no help either: it is transient and returns to false on close.
 *
 * Written where the survey is opened, and read by the pop-up banner (#885563), which yields to the
 * survey for the rest of the session rather than stacking a second centred dialog over it.
 * Cleared on logout with the other session-scoped flags.
 */
export const OSAT_SHOWN_KEY = 'osat:shown';

/** Whether the OSAT survey opened in this tab-session. */
export const getOsatShown = (): boolean => {
  try {
    return sessionStorage.getItem(OSAT_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
};

/** Mark that the OSAT survey opened. No-op if storage is unavailable. */
export const markOsatShown = (): void => {
  try {
    sessionStorage.setItem(OSAT_SHOWN_KEY, '1');
  } catch {
    // sessionStorage unavailable — the banner degrades to not yielding.
  }
};

/** Clear the shown flag. Called on logout / session-clear alongside `clearOsatEvaluated`. */
export const clearOsatShown = (): void => {
  try {
    sessionStorage.removeItem(OSAT_SHOWN_KEY);
  } catch {
    // Nothing to clear.
  }
};
